import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useState } from "react";
import AppContext from "../../context/AppContext";
import {
  updateProject,
  getProjectById,
  getProjectList,
  getWorkflowListByProjectId,
  getWorkflowFieldValues,
  updateProjectDeleteStatus,
} from "../../service/User";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ReactSelector from "../common/CommonReactSelector";
import { toast } from "../../utils/toast";
import { getPermissionByScreenCode, DCRC_CONFIG_SCREEN_CODE, PROJECT_SCREEN_CODE } from "../../utils/permissions";
import ModalCloseButton from "../common/ModalCloseButton";
import { isRTL } from "../../utils/rtl";

const DCRC_BASE_STEP_TYPES = [
  { stepType: "ND", label: "ND" },
  { stepType: "DC", label: "DC" },
  { stepType: "SV", label: "SV" },
  { stepType: "RC", label: "RC" },
];
const POLE_DC_STEP = { stepType: "POLE_DC", label: "POLE_DC" };

const defaultDcrcSubscription = () => ({
  isAutoRcEnabled: true,
  isPoleDcEnabled: false,
  isInstallmentPlanEnabled: false,
  minPaymentPercent: 10.0,
  minOutstandingAmount: 50.0,
  svMaxIterations: 3,
});

const NEXT_STEP_OPTIONS_BASE = [
  { value: "ND", label: "ND" },
  { value: "RC", label: "RC" },
  { value: "DC", label: "DC" },
  { value: "SV", label: "SV" },
  { value: "__none__", label: "None" },
];
const NEXT_STEP_OPTIONS_WITH_POLE_DC = [
  { value: "ND", label: "ND" },
  { value: "RC", label: "RC" },
  { value: "DC", label: "DC" },
  { value: "SV", label: "SV" },
  { value: "POLE_DC", label: "POLE_DC" },
  { value: "__none__", label: "None" },
];

const defaultWorkflowConfig = (stepType) => ({
  workflowId: null,
  stepType,
  delayDays: 0,
  endCycleFieldKey: null,
  endCycleValues: [],
  nextStepSuccess: null,
  nextStepFailure: null,
  poleDcReasonUniqueKey: null,
  poleDcReasonValues: [],
});

const mapApiConfigToForm = (c, projectDcrcPoleDcReasons) => {
  const poleReasons = Array.isArray(projectDcrcPoleDcReasons) ? projectDcrcPoleDcReasons : [];
  const poleUniqueKey = poleReasons[0]?.uniqueKey ?? null;
  const isDcWithPoleDc = c.stepType === "DC" && (c.nextStepFailure === "POLE_DC" || poleReasons.length > 0);
  return {
    workflowId: c.workflowId ?? null,
    stepType: c.stepType,
    delayDays: c.delayDays ?? 0,
    endCycleFieldKey: c.endCycleFieldKey ?? null,
    endCycleValues: Array.isArray(c.endCycleValues) ? c.endCycleValues : [],
    nextStepSuccess: c.nextStepSuccess ?? null,
    nextStepFailure: c.nextStepFailure ?? null,
    poleDcReasonUniqueKey: isDcWithPoleDc ? poleUniqueKey : null,
    poleDcReasonValues: isDcWithPoleDc
      ? poleReasons.filter((r) => r.uniqueKey === poleUniqueKey && r.value != null).map((r) => r.value)
      : [],
  };
};

const EditProject = ({ open, setOpen, project, departmentId, setProjectData }) => {
  const { theme, langMode, userType } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const [projectName, setProjectName] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [dcrcSubscriptionForm, setDcrcSubscriptionForm] = useState(defaultDcrcSubscription());
  const [dcrcWorkflowConfigs, setDcrcWorkflowConfigs] = useState(() =>
    DCRC_BASE_STEP_TYPES.map(({ stepType }) => defaultWorkflowConfig(stepType))
  );
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [workflowFieldOptions, setWorkflowFieldOptions] = useState({});
  const [restoring, setRestoring] = useState(false);
  const isDark = theme === "dark";
  const isContrast = theme === "high-contrast";
  const isSuperAdmin = userType === "super_admin";
    const isAdmin = userType === "admin";
  const isOfficeUser = userType === "office_user";
  const canUpdateDcrc = (isSuperAdmin || isAdmin || isOfficeUser) && Boolean(getPermissionByScreenCode(DCRC_CONFIG_SCREEN_CODE)?.canUpdate);
  const canUpdateProject = (isSuperAdmin || isAdmin || isOfficeUser) && Boolean(getPermissionByScreenCode(PROJECT_SCREEN_CODE)?.canUpdate);
  const showDcrcSections = (isSuperAdmin || isAdmin || isOfficeUser) && projectDetails?.isDcrcEnabled;
  const isDeleted = projectDetails?.isDeleted === true;
  const isPoleDcEnabled = dcrcSubscriptionForm?.isPoleDcEnabled === true;
  const hasPoleDcStep = dcrcWorkflowConfigs.some((c) => c.stepType === "POLE_DC");
  const stepTypesForDisplay = isPoleDcEnabled ? [...DCRC_BASE_STEP_TYPES, POLE_DC_STEP] : DCRC_BASE_STEP_TYPES;
  const nextStepOptions = (isPoleDcEnabled || hasPoleDcStep ? NEXT_STEP_OPTIONS_WITH_POLE_DC : NEXT_STEP_OPTIONS_BASE).map(
    (o) => (o.value === "__none__" ? { ...o, label: translations[langMode]?.orgNone ?? o.label } : o)
  );

  useEffect(() => {
    if (!open || !project?.id) {
      setProjectDetails(null);
      return;
    }
    const load = async () => {
      setLoadingProject(true);
      const full = await getProjectById(project.id);
      setProjectDetails(full || project);
      setLoadingProject(false);
    };
    load();
  }, [open, project?.id]);

  useEffect(() => {
    if (!open || !projectDetails) return;
    setProjectName(projectDetails.name ?? project?.name ?? "");
    const sub = projectDetails.dcrcSubscription;
    if (sub) {
      setDcrcSubscriptionForm({
        isAutoRcEnabled: sub.isAutoRcEnabled ?? sub.autoRcEnabled ?? true,
        isPoleDcEnabled: sub.isPoleDcEnabled ?? sub.poleDcEnabled ?? false,
        isInstallmentPlanEnabled: sub.isInstallmentPlanEnabled ?? sub.installmentPlanEnabled ?? false,
        minPaymentPercent: Number(sub.minPaymentPercent) ?? 10.0,
        minOutstandingAmount: Number(sub.minOutstandingAmount) ?? 50.0,
        svMaxIterations: Number(sub.svMaxIterations) ?? 3,
      });
    } else {
      setDcrcSubscriptionForm(defaultDcrcSubscription());
    }
    const configs = projectDetails.dcrcWorkflowConfigs;
    const dcrcPoleDcReasons = Array.isArray(projectDetails.dcrcPoleDcReasons) ? projectDetails.dcrcPoleDcReasons : [];
    const poleEnabled = sub ? (sub.isPoleDcEnabled ?? sub.poleDcEnabled ?? false) : false;
    const stepTypes = poleEnabled ? [...DCRC_BASE_STEP_TYPES.map((s) => s.stepType), "POLE_DC"] : DCRC_BASE_STEP_TYPES.map((s) => s.stepType);
    if (Array.isArray(configs) && configs.length > 0) {
      const mapped = stepTypes.map((stepType) => {
        const found = configs.find((c) => c.stepType === stepType);
        return found ? mapApiConfigToForm(found, dcrcPoleDcReasons) : defaultWorkflowConfig(stepType);
      });
      setDcrcWorkflowConfigs(mapped);
      const workflowIds = [...new Set(mapped.filter((c) => c.workflowId).map((c) => c.workflowId))];
      const initialOptions = workflowIds.length ? Object.fromEntries(workflowIds.map((id) => [id, null])) : {};
      setWorkflowFieldOptions(initialOptions);
      workflowIds.forEach((workflowId) => {
        getWorkflowFieldValues(workflowId)
          .then(({ fieldKeyOptions, valueOptions, rawFields }) => {
            setWorkflowFieldOptions((prev) => ({ ...prev, [workflowId]: { fieldKeyOptions, valueOptions, rawFields } }));
          })
          .catch(() => {
            setWorkflowFieldOptions((prev) => ({ ...prev, [workflowId]: { fieldKeyOptions: [], valueOptions: [], rawFields: [] } }));
          });
      });
    } else {
      setDcrcWorkflowConfigs(stepTypes.map((stepType) => defaultWorkflowConfig(stepType)));
      setWorkflowFieldOptions({});
    }
  }, [open, projectDetails]);

  useEffect(() => {
    if (!open || !project?.id || !isSuperAdmin) {
      setWorkflowOptions([]);
      return;
    }
    const fetchWorkflows = async () => {
      setLoadingWorkflows(true);
      try {
        const workflows = await getWorkflowListByProjectId(project.id);
        const list = Array.isArray(workflows) ? workflows : [];
        setWorkflowOptions(
          list.map((w) => ({ value: w.workflowId, label: w.workflowName || `Workflow ${w.workflowId}` }))
        );
      } catch (err) {
        setWorkflowOptions([]);
      } finally {
        setLoadingWorkflows(false);
      }
    };
    fetchWorkflows();
  }, [open, project?.id, isSuperAdmin]);

  const updateConfig = (index, field, value) => {
    if (!canUpdateDcrc) return;
    setDcrcWorkflowConfigs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const setSubscriptionField = (field, value) => {
    if (!canUpdateDcrc) return;
    setDcrcSubscriptionForm((prev) => ({ ...prev, [field]: value }));
    if (field === "isPoleDcEnabled") {
      if (value) {
        setDcrcWorkflowConfigs((p) => [...p, defaultWorkflowConfig("POLE_DC")]);
      } else {
        setDcrcWorkflowConfigs((p) => p.filter((c) => c.stepType !== "POLE_DC"));
      }
    }
  };

  const fetchFieldValuesForWorkflow = async (workflowId) => {
    if (!workflowId) return;
    setWorkflowFieldOptions((prev) => ({ ...prev, [workflowId]: null }));
    try {
      const { fieldKeyOptions, valueOptions, rawFields } = await getWorkflowFieldValues(workflowId);
      setWorkflowFieldOptions((prev) => ({ ...prev, [workflowId]: { fieldKeyOptions, valueOptions, rawFields } }));
      const firstUniqueKey = fieldKeyOptions?.[0]?.value;
      if (firstUniqueKey) {
        setDcrcWorkflowConfigs((prev) =>
          prev.map((c) => (c.workflowId === workflowId ? { ...c, endCycleFieldKey: firstUniqueKey } : c))
        );
      }
    } catch {
      setWorkflowFieldOptions((prev) => ({ ...prev, [workflowId]: { fieldKeyOptions: [], valueOptions: [], rawFields: [] } }));
    }
  };

  const handleSave = async () => {
    if (!project?.id) return;
    if (!projectName?.trim()) {
      toast.warning(translations[langMode].pleaseentername || "Please enter project name");
      return;
    }
    if (showDcrcSections && canUpdateDcrc) {
      const missing = dcrcWorkflowConfigs.some((c) => !c.workflowId);
      if (missing) {
        const stepsList = isPoleDcEnabled ? "ND, DC, SV, RC, POLE_DC" : "ND, DC, SV, RC";
        toast.warning(
          (translations[langMode]?.orgSelectActivityAllSteps ?? "Please select Activity (workflow) for all DCRC steps ({steps}).").replace("{steps}", stepsList)
        );
        return;
      }
    }
    setIsLoader(true);
    try {
      const payload = {
        name: projectName.trim(),
        departmentId: Number(project.departmentId ?? departmentId),
        isDcrcEnabled: projectDetails?.isDcrcEnabled ?? false,
      };
      if (showDcrcSections && canUpdateDcrc) {
        payload.dcrcSubscription = {
          isAutoRcEnabled: dcrcSubscriptionForm.isAutoRcEnabled ?? true,
          isPoleDcEnabled: dcrcSubscriptionForm.isPoleDcEnabled ?? false,
          isInstallmentPlanEnabled: dcrcSubscriptionForm.isInstallmentPlanEnabled ?? false,
          minPaymentPercent: Number(dcrcSubscriptionForm.minPaymentPercent) ?? 10.0,
          minOutstandingAmount: Number(dcrcSubscriptionForm.minOutstandingAmount) ?? 50.0,
          svMaxIterations: Number(dcrcSubscriptionForm.svMaxIterations) ?? 3,
        };
        payload.dcrcWorkflowConfigs = dcrcWorkflowConfigs.map((c) => ({
          workflowId: Number(c.workflowId),
          stepType: c.stepType,
          delayDays: Number(c.delayDays) || 0,
          endCycleFieldKey: c.endCycleFieldKey || null,
          endCycleValues: Array.isArray(c.endCycleValues) ? c.endCycleValues : [],
          nextStepSuccess: c.nextStepSuccess === "__none__" || c.nextStepSuccess === "" ? null : (c.nextStepSuccess || null),
          nextStepFailure: c.nextStepFailure === "__none__" || c.nextStepFailure === "" ? null : (c.nextStepFailure || null),
        }));
        const poleDcConfig = dcrcWorkflowConfigs.find(
          (c) => c.stepType === "DC" && c.nextStepFailure === "POLE_DC" && c.poleDcReasonUniqueKey && c.poleDcReasonValues?.length
        );
        payload.dcrcPoleDcReasons = poleDcConfig
          ? poleDcConfig.poleDcReasonValues.map((value) => ({ uniqueKey: poleDcConfig.poleDcReasonUniqueKey, value }))
          : [];
      }
      await updateProject(project.id, payload);
      if (departmentId) {
        const result = await getProjectList(departmentId);
        setProjectData(result);
      }
      setOpen(false);
    } catch (err) {
      console.error("Error updating project:", err);
    } finally {
      setIsLoader(false);
    }
  };

  const handleRestore = async () => {
    if (!project?.id) return;
    setRestoring(true);
    try {
      await updateProjectDeleteStatus(project.id, false);
      if (departmentId) {
        const result = await getProjectList(departmentId);
        setProjectData(result);
      }
      toast.success(translations[langMode]?.orgProjectVisible ?? "Project visible again");
      setOpen(false);
    } catch (err) {
      console.error("Error showing project:", err);
      toast.error(translations[langMode].orgFailedShowProject || "Failed to show project");
    } finally {
      setRestoring(false);
    }
  };

  const inputClass = `w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50
    ${isDark ? "bg-[#2C2C2C] text-white border-gray-600" : "border-gray-300"}
    ${isContrast ? "border-black text-black" : ""}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDark ? "text-gray-200" : isContrast ? "text-black" : "text-gray-700"}`;
  const sectionClass = `rounded-lg border p-4 ${isDark ? "border-gray-600 bg-[#252525]" : isContrast ? "border-black bg-gray-50" : "border-gray-200 bg-gray-50"}`;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`max-h-[calc(100dvh-1rem)] w-full max-w-2xl transform flex flex-col overflow-hidden rounded-xl shadow-xl transition-all sm:max-h-[90dvh] sm:rounded-2xl
                  ${isDark ? "bg-[#1E1E1E] text-white" : isContrast ? "bg-white text-black border border-black" : "bg-white text-gray-800"}`}
              >
                <div className="flex shrink-0 justify-between items-center px-3 pb-4 pt-3 sm:px-6 sm:pt-6">
                  <Dialog.Title className="text-lg font-semibold" data-testid="edit-organization-modal-title">
                    {translations[langMode].edit} {translations[langMode].projectname}
                  </Dialog.Title>
                  <ModalCloseButton onClick={() => setOpen(false)} />
                </div>
                {loadingProject ? (
                  <p className="text-sm text-gray-500 px-3 py-4 sm:px-6">{translations[langMode]?.orgLoadingProject ?? "Loading project…"}</p>
                ) : (
                  <>
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-3 pb-4 sm:px-6">
                      <div>
                        <label className={labelClass}>{translations[langMode].projectname}</label>
                        <input
                          type="text"
                          value={projectName}
                          disabled={!canUpdateProject}
                          onChange={(e) => setProjectName(e.target.value)}
                          placeholder={translations[langMode].entername}
                          className={`${inputClass} ${!canUpdateProject ? "cursor-not-allowed opacity-60" : ""}`}
                          data-testid="edit-organization-modal-form-project-name-input"
                        />
                      </div>

                      {showDcrcSections && (
                        <div className="space-y-4" aria-disabled={!canUpdateDcrc}>
                          <div className={sectionClass} data-testid="dcrc-configuration-form">
                            <h4 className={`text-sm font-semibold mb-3 ${isDark ? "text-gray-200" : "text-gray-800"}`} data-testid="dcrc-configuration-form-title">{translations[langMode]?.orgDcrcSubscription ?? "DCRC Subscription"}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="flex items-center gap-3">
                                <span className={labelClass} data-testid="dcrc-configuration-form-auto-rc-label">{translations[langMode]?.orgAutoRc ?? "Auto RC"}</span>
                                <button
                                  type="button"
                                  role="switch"
                                  disabled={!canUpdateDcrc}
                                  aria-checked={dcrcSubscriptionForm.isAutoRcEnabled}
                                  onClick={() => setSubscriptionField("isAutoRcEnabled", !dcrcSubscriptionForm.isAutoRcEnabled)}
                                  className={`relative inline-flex h-6 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${dcrcSubscriptionForm.isAutoRcEnabled ? (isDark ? "bg-blue-500" : "bg-blue-600") : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                                  data-testid="dcrc-configuration-form-auto-rc-check"
                                >
                                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${dcrcSubscriptionForm.isAutoRcEnabled ? (isRtl ? "-translate-x-4" : "translate-x-4") : "translate-x-0.5"}`} />
                                </button>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={labelClass} data-testid="dcrc-configuration-form-pole-dc-label">{translations[langMode]?.orgPoleDc ?? "Pole DC"}</span>
                                <button
                                  type="button"
                                  role="switch"
                                  disabled={!canUpdateDcrc}
                                  aria-checked={dcrcSubscriptionForm.isPoleDcEnabled}
                                  onClick={() => setSubscriptionField("isPoleDcEnabled", !dcrcSubscriptionForm.isPoleDcEnabled)}
                                  className={`relative inline-flex h-6 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${dcrcSubscriptionForm.isPoleDcEnabled ? (isDark ? "bg-blue-500" : "bg-blue-600") : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                                  data-testid="dcrc-configuration-form-pole-dc-check"
                                >
                                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${dcrcSubscriptionForm.isPoleDcEnabled ? (isRtl ? "-translate-x-4" : "translate-x-4") : "translate-x-0.5"}`} />
                                </button>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={labelClass} data-testid="dcrc-configuration-form-installment-plan-label">{translations[langMode]?.orgInstallmentPlan ?? "Installment plan"}</span>
                                <button
                                  type="button"
                                  role="switch"
                                  disabled={!canUpdateDcrc}
                                  aria-checked={dcrcSubscriptionForm.isInstallmentPlanEnabled}
                                  onClick={() => setSubscriptionField("isInstallmentPlanEnabled", !dcrcSubscriptionForm.isInstallmentPlanEnabled)}
                                  className={`relative inline-flex h-6 w-10 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${dcrcSubscriptionForm.isInstallmentPlanEnabled ? (isDark ? "bg-blue-500" : "bg-blue-600") : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                                  data-testid="dcrc-configuration-form-installment-plan-check"
                                >
                                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${dcrcSubscriptionForm.isInstallmentPlanEnabled ? (isRtl ? "-translate-x-4" : "translate-x-4") : "translate-x-0.5"}`} />
                                </button>
                              </div>
                              <div>
                                <label className={labelClass} data-testid="dcrc-configuration-form-min-payment-percent-label">{translations[langMode]?.orgMinPaymentPercent ?? "Min payment %"}</label>
                                <input type="number" min={0} step={0.1} disabled={!canUpdateDcrc} value={dcrcSubscriptionForm.minPaymentPercent} onChange={(e) => setSubscriptionField("minPaymentPercent", e.target.value)} className={`${inputClass} ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : ""}`}
                                 data-testid="dcrc-configuration-form-min-payment-percent-input"
                                />
                              </div>
                              <div>
                                <label className={labelClass} data-testid="dcrc-configuration-form-min-outstanding-amount-label">{translations[langMode]?.orgMinOutstandingAmount ?? "Min outstanding amount"}</label>
                                <input type="number" min={0} step={0.1} disabled={!canUpdateDcrc} value={dcrcSubscriptionForm.minOutstandingAmount} onChange={(e) => setSubscriptionField("minOutstandingAmount", e.target.value)} className={`${inputClass} ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : ""}`}
                                 data-testid="dcrc-configuration-form-min-outstanding-amount-input"
                                />
                              </div>
                              <div>
                                <label className={labelClass} data-testid="dcrc-configuration-form-sv-max-iterations-label">{translations[langMode]?.orgSvMaxIterations ?? "SV max iterations"}</label>
                                <input type="number" min={0} disabled={!canUpdateDcrc} value={dcrcSubscriptionForm.svMaxIterations} onChange={(e) => setSubscriptionField("svMaxIterations", e.target.value)} className={`${inputClass} ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : ""}`}
                                 data-testid="dcrc-configuration-form-sv-max-iterations-input"
                                />
                              </div>
                            </div>
                          </div>
                          <div className={sectionClass}>
                            <h4 className={`text-sm font-semibold mb-3 ${isDark ? "text-gray-200" : "text-gray-800"}`} data-testid="dcrc-workflow-config-title">{isPoleDcEnabled
                              ? (translations[langMode]?.orgDcrcWorkflowConfigPoleDc ?? "DCRC Workflow Config (Activity required for each step, including POLE_DC)")
                              : (translations[langMode]?.orgDcrcWorkflowConfig ?? "DCRC Workflow Config (Activity required for each step)")}</h4>
                            {loadingWorkflows && <p className="text-sm text-gray-500 mb-3">{translations[langMode]?.orgLoadingActivities ?? "Loading activities…"}</p>}
                            <div className="space-y-4">
                              {dcrcWorkflowConfigs.map((config, index) => (
                                <div key={config.stepType} className={`rounded border p-3 ${isDark ? "border-gray-600 bg-[#1E1E1E]" : "border-gray-200 bg-white"}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-sm font-medium min-w-[32px] ${isDark ? "text-blue-300" : "text-blue-700"}`} data-testid={`dcrc-workflow-config-step-${config.stepType}`}>{config.stepType}</span>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="sm:col-span-2">
                                      <label className={labelClass} data-testid={`dcrc-workflow-config-activity-label-${config.stepType}`}>{translations[langMode]?.orgActivityWorkflow ?? "Activity (Workflow)"} <span className="text-red-500">*</span></label>
                                      <ReactSelector
                                        isDisabled={!canUpdateDcrc}
                                        options={workflowOptions}
                                        value={workflowOptions.find((o) => o.value === config.workflowId)}
                                        onChange={(opt) => {
                                          const wfId = opt?.value ?? null;
                                          updateConfig(index, "workflowId", wfId);
                                          if (wfId) fetchFieldValuesForWorkflow(wfId);
                                          else {
                                            updateConfig(index, "endCycleValues", []);
                                            updateConfig(index, "endCycleFieldKey", null);
                                          }
                                        }}
                                        placeholder={translations[langMode]?.orgSelectActivity ?? "Select activity"}
                                        dataTestId={`dcrc-workflow-config-activity-select-${config.stepType}`}
                                        isClearable
                                        height="36px"
                                        menuPortalTarget={document.body}
                                      />
                                    </div>
                                    <div>
                                      <label className={labelClass} data-testid={`dcrc-workflow-config-delay-label-${config.stepType}`}>{translations[langMode]?.orgDelayDays ?? "Delay (days)"}</label>
                                      <input type="number" min={0} disabled={!canUpdateDcrc} value={config.delayDays} onChange={(e) => updateConfig(index, "delayDays", e.target.value)} className={`${inputClass} ${!canUpdateDcrc ? "cursor-not-allowed opacity-60" : ""}`} data-testid={`dcrc-workflow-config-delay-input-${config.stepType}`} />
                                    </div>
                                    <div>
                                      <label className={labelClass} data-testid={`dcrc-workflow-config-end-cycle-field-label-${config.stepType}`}>{translations[langMode]?.orgEndCycleField ?? "End cycle field"}</label>
                                      {config.workflowId ? (
                                        workflowFieldOptions[config.workflowId] == null ? (
                                          <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translations[langMode]?.orgLoading ?? "Loading…"}</p>
                                        ) : (() => {
                                            const fieldOpts = workflowFieldOptions[config.workflowId]?.fieldKeyOptions || [];
                                            return fieldOpts.length ? (
                                              <ReactSelector
                                                isDisabled={!canUpdateDcrc}
                                                options={fieldOpts}
                                                value={fieldOpts.find((o) => o.value === config.endCycleFieldKey) ?? null}
                                                onChange={(opt) => {
                                                  updateConfig(index, "endCycleFieldKey", opt?.value ?? null);
                                                  updateConfig(index, "endCycleValues", []);
                                                }}
                                                placeholder={translations[langMode]?.orgSelectField ?? "Select field"}
                                                dataTestId={`dcrc-workflow-config-end-cycle-field-select-${config.stepType}`}
                                                isClearable
                                                height="36px"
                                                menuPortalTarget={document.body}
                                              />
                                            ) : (
                                              <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translations[langMode]?.orgNoFieldsInWorkflow ?? "No fields in workflow"}</p>
                                            );
                                          })()
                                      ) : (
                                        <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translations[langMode]?.orgSelectActivityFirst ?? "Select an activity first"}</p>
                                      )}
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className={labelClass} data-testid={`dcrc-workflow-config-end-cycle-values-label-${config.stepType}`}>{translations[langMode]?.orgEndCycleValues ?? "End cycle values"}</label>
                                      {config.workflowId ? (
                                        workflowFieldOptions[config.workflowId] == null ? (
                                          <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translations[langMode]?.orgLoadingOptions ?? "Loading options…"}</p>
                                        ) : (
                                          <ReactSelector
                                            isMulti
                                            isDisabled={!canUpdateDcrc}
                                            options={workflowFieldOptions[config.workflowId]?.valueOptions || []}
                                            value={(Array.isArray(config.endCycleValues) ? config.endCycleValues : []).map((v) => ({ value: v, label: v }))}
                                            onChange={(selected) => updateConfig(index, "endCycleValues", selected ? selected.map((s) => s.value) : [])}
                                            placeholder={translations[langMode]?.orgSelectValues ?? "Select values"}
                                            dataTestId={`dcrc-workflow-config-end-cycle-values-select-${config.stepType}`}
                                            isClearable
                                            height="36px"
                                            menuPortalTarget={document.body}
                                          />
                                        )
                                      ) : (
                                        <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translations[langMode]?.orgSelectActivityFirst ?? "Select an activity first"}</p>
                                      )}
                                    </div>
                                    <div>
                                      <label className={labelClass} data-testid={`dcrc-workflow-config-next-step-success-label-${config.stepType}`}>{translations[langMode]?.orgNextStepSuccess ?? "Next step (success)"}</label>
                                      <ReactSelector
                                        isDisabled={!canUpdateDcrc}
                                        options={nextStepOptions}
                                        value={nextStepOptions.find((o) => o.value === (config.nextStepSuccess || "__none__"))}
                                        onChange={(opt) => updateConfig(index, "nextStepSuccess", opt?.value === "__none__" ? null : opt?.value ?? null)}
                                        placeholder={translations[langMode]?.select ?? "Select"}
                                        isClearable
                                        height="36px"
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        maxMenuHeight={150}
                                        dataTestId={`dcrc-workflow-config-next-step-success-select-${config.stepType}`}
                                      />
                                    </div>
                                    <div>
                                      <label className={labelClass} data-testid={`dcrc-workflow-config-next-step-failure-label-${config.stepType}`}>{translations[langMode]?.orgNextStepFailure ?? "Next step (failure)"}</label>
                                      <ReactSelector
                                        isDisabled={!canUpdateDcrc}
                                        options={nextStepOptions}
                                        value={nextStepOptions.find((o) => o.value === (config.nextStepFailure || "__none__"))}
                                        onChange={(opt) => {
                                          const val = opt?.value === "__none__" ? null : (opt?.value ?? null);
                                          updateConfig(index, "nextStepFailure", val);
                                          if (val !== "POLE_DC") {
                                            updateConfig(index, "poleDcReasonUniqueKey", null);
                                            updateConfig(index, "poleDcReasonValues", []);
                                          }
                                        }}
                                        placeholder={translations[langMode]?.select ?? "Select"}
                                        isClearable
                                        height="36px"
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        maxMenuHeight={150}
                                        dataTestId={`dcrc-workflow-config-next-step-failure-select-${config.stepType}`}
                                      />
                                    </div>
                                    {config.stepType === "DC" && config.nextStepFailure === "POLE_DC" && (
                                      <div className="sm:col-span-2 rounded border p-3 mt-2 border-blue-200 bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-700">
                                        <h5 className={`text-sm font-semibold mb-3 ${isDark ? "text-blue-200" : "text-blue-800"}`}>Pole DC Reasons</h5>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                          <div>
                                            <label className={labelClass} data-testid={`dcrc-workflow-config-pole-dc-reasons-field-label-${config.stepType}`}>{translations[langMode]?.orgField ?? "Field"}</label>
                                            {workflowFieldOptions[config.workflowId]?.fieldKeyOptions?.length ? (
                                              <ReactSelector
                                                isDisabled={!canUpdateDcrc}
                                                options={workflowFieldOptions[config.workflowId].fieldKeyOptions}
                                                value={workflowFieldOptions[config.workflowId].fieldKeyOptions.find((o) => o.value === config.poleDcReasonUniqueKey)}
                                                onChange={(opt) => {
                                                  updateConfig(index, "poleDcReasonUniqueKey", opt?.value ?? null);
                                                  updateConfig(index, "poleDcReasonValues", []);
                                                }}
                                                placeholder={translations[langMode]?.orgSelectField ?? "Select field"}
                                                dataTestId={`dcrc-workflow-config-pole-dc-reasons-field-select-${config.stepType}`}
                                                isClearable
                                                height="36px"
                                                menuPortalTarget={document.body}
                                              />
                                            ) : (
                                              <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translations[langMode]?.orgLoadActivityFirst ?? "Load activity first"}</p>
                                            )}
                                          </div>
                                          <div>
                                            <label className={labelClass} data-testid={`dcrc-workflow-config-pole-dc-reasons-value-label-${config.stepType}`}>{translations[langMode]?.orgValues ?? "Values"}</label>
                                            {(() => {
                                              const rawFields = workflowFieldOptions[config.workflowId]?.rawFields || [];
                                              const selectedField = rawFields.find((f) => f.uniqueKey === config.poleDcReasonUniqueKey);
                                              const valueOptions = (selectedField?.values || []).map((v) => ({ value: v, label: v }));
                                              return valueOptions.length ? (
                                                <ReactSelector
                                                  isMulti
                                                  isDisabled={!canUpdateDcrc}
                                                  options={valueOptions}
                                                  value={(Array.isArray(config.poleDcReasonValues) ? config.poleDcReasonValues : []).map((v) => ({ value: v, label: v }))}
                                                  onChange={(selected) => updateConfig(index, "poleDcReasonValues", selected ? selected.map((s) => s.value) : [])}
                                                  placeholder={translations[langMode]?.orgSelectValues ?? "Select values"}
                                                  dataTestId={`dcrc-workflow-config-pole-dc-reasons-value-select-${config.stepType}`}
                                                  isClearable
                                                  height="36px"
                                                  menuPortalTarget={document.body}
                                                />
                                              ) : (
                                                <p className={`text-sm py-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                                                  {config.poleDcReasonUniqueKey
                                                    ? (translations[langMode]?.orgNoValuesForField ?? "No values for this field")
                                                    : (translations[langMode]?.orgSelectFieldFirst ?? "Select field first")}
                                                </p>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      className={`flex shrink-0 justify-end items-center gap-2 border-t px-3 py-3 sm:gap-3 sm:px-6 sm:py-4 ${
                        isDark ? "border-gray-700 bg-[#1E1E1E]" : isContrast ? "border-black bg-white" : "border-gray-200 bg-white"
                      }`}
                    >
                      <CommonButton colorIntent="danger" onClick={() => setOpen(false)} variant="danger" wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial" className="w-full !h-10 !rounded-lg justify-center" data-testid="edit-organization-modal-form-cancel-button">
                        {translations[langMode].cancle}
                      </CommonButton>
                      {isDeleted ? (
                        <CommonButton colorIntent="success" onClick={handleRestore} variant="primary" disabled={restoring} wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial" className="w-full !h-10 !rounded-lg justify-center" data-testid="edit-organization-modal-form-restore-button">
                          {restoring ? (translations[langMode]?.orgShowing ?? "Showing…") : (translations[langMode]?.orgShowProject ?? "Show project")}
                        </CommonButton>
                      ) : (
                        <CommonButton colorIntent="success" onClick={handleSave} variant="primary" wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial" className="w-full !h-10 !rounded-lg justify-center" data-testid="edit-organization-modal-form-save-button">
                          {isLoader ? translations[langMode].Saving : translations[langMode].save}
                        </CommonButton>
                      )}
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditProject;
